import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Divider, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal, Empty, Loader } from "@zfp-akademie-a-s/zfp-design";
import { FormRadioGroup, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { toRadioOptions } from "Forms/fieldOptions";
import {
  getListContractProposalsQueryKey,
  useGetAnalysisSuitabilityStatement,
  useRejectAnalysisInvestmentCase,
  useSaveAnalysisSuitabilityStatement,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as yup from "yup";
import { useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";

type JustificationFormType = {
  suitabilityInvestmentPlan?: string;
  suitabilityKnowledgeAndExperience?: string;
  suitabilityInvestmentHorizon?: string;
  suitabilityFinanceSituation?: string;
  suitabilityRiskTolerance?: string;
  suitabilityESG?: string;
  suitabilityAdditionalStatement?: string;
  isClientAgreed: string;
  agentStatement?: string;
  statementDate: Date | null;
};

const getEndOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const get14DaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isDisagreed = (isClientAgreed: unknown) => (isClientAgreed as string[])[0] === "false";

const MIN_TEXT_LENGTH = 10;
const MAX_TEXT_LENGTH = 300;

const withMinLength = (schema: yup.StringSchema, t: (key: any, options?: any) => string) =>
  schema
    .test(
      "min-length",
      t(Resources.Validation.String.Min, { count: MIN_TEXT_LENGTH }),
      (value) => !value || value.trim().length >= MIN_TEXT_LENGTH,
    )
    .max(MAX_TEXT_LENGTH, t(Resources.Validation.String.Max, { count: MAX_TEXT_LENGTH }));

const getSchema = (t: (key: any, options?: any) => string) =>
  yup.object({
    suitabilityInvestmentPlan: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityKnowledgeAndExperience: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityInvestmentHorizon: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityFinanceSituation: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityRiskTolerance: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityESG: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityAdditionalStatement: withMinLength(yup.string(), t).optional(),
    isClientAgreed: yup.string().required(t(Resources.Validation.Required)),
    agentStatement: withMinLength(yup.string().trim(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.required(t(Resources.Validation.Required)) : schema.optional(),
    ),
    statementDate: yup
      .date()
      .typeError(t(Resources.Validation.BadValue))
      .required(t(Resources.Validation.Required))
      .max(getEndOfToday(), t(Resources.Validation.DateMustNotBeFuture))
      .min(get14DaysAgo(), t(Resources.Validation.DateMustNotBeOlderThan14Days))
      .nullable(),
  });

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const GoalFulfillmentJustification = ({ isOpen, onClose }: Props) => {
  const { t } = useResource();
  const r = Resources.AnalysisDetail.GoalsFulfillment.Nsure.Justification;
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { fetchGrowUpContractingLink } = useGoalsFulfillmentContext();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useGetAnalysisSuitabilityStatement(analysisId, versionId);
  const { mutate: save, isPending: isSaving } = useSaveAnalysisSuitabilityStatement();
  const { mutate: rejectCase, isPending: isRejecting } = useRejectAnalysisInvestmentCase();

  const methods = useForm<JustificationFormType>({
    resolver: yupResolver(getSchema(t)),
    mode: "onChange",
    defaultValues: {
      isClientAgreed: "true",
      statementDate: new Date(),
    },
  });

  const { control, watch, reset, clearErrors, handleSubmit } = methods;

  useEffect(() => {
    const s = data?.suitabilityStatement;
    if (!s) return;
    reset({
      suitabilityInvestmentPlan: s.suitabilityInvestmentPlan ?? "",
      suitabilityKnowledgeAndExperience: s.suitabilityKnowledgeAndExperience ?? "",
      suitabilityInvestmentHorizon: s.suitabilityInvestmentHorizon ?? "",
      suitabilityFinanceSituation: s.suitabilityFinanceSituation ?? "",
      suitabilityRiskTolerance: s.suitabilityRiskTolerance ?? "",
      suitabilityESG: s.suitabilityESG ?? "",
      suitabilityAdditionalStatement: s.suitabilityAdditionalStatement ?? "",
      isClientAgreed: s.isClientAgreed === false ? "false" : "true",
      agentStatement: s.agentStatement ?? "",
      statementDate: s.statementDate ? new Date(s.statementDate) : null,
    });
  }, [data, reset]);

  const isClientDisagreed = watch("isClientAgreed") === "false";

  useEffect(() => {
    if (isClientDisagreed) {
      clearErrors([
        "suitabilityInvestmentPlan",
        "suitabilityKnowledgeAndExperience",
        "suitabilityInvestmentHorizon",
        "suitabilityFinanceSituation",
        "suitabilityRiskTolerance",
        "suitabilityESG",
      ]);
    }
  }, [isClientDisagreed, clearErrors]);

  const onSave = (formData: JustificationFormType) => {
    const onError = () => toast.error(t(Resources.AnalysisDetail.Toast.ActionError));

    if (isClientDisagreed) {
      rejectCase(
        {
          analysisId,
          versionId,
          data: { reason: formData.agentStatement ?? "" },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
            toast.success(t(Resources.AnalysisDetail.Toast.InvestmentCaseRejected));
            onClose();
          },
          onError,
        },
      );
      return;
    }

    save(
      {
        analysisId,
        versionId,
        data: {
          suitabilityInvestmentPlan: formData.suitabilityInvestmentPlan,
          suitabilityKnowledgeAndExperience: formData.suitabilityKnowledgeAndExperience,
          suitabilityInvestmentHorizon: formData.suitabilityInvestmentHorizon,
          suitabilityFinanceSituation: formData.suitabilityFinanceSituation,
          suitabilityRiskTolerance: formData.suitabilityRiskTolerance,
          suitabilityESG: formData.suitabilityESG,
          suitabilityAdditionalStatement: formData.suitabilityAdditionalStatement ?? null,
          suitabilityDate: formData.statementDate ? formData.statementDate.toISOString() : null,
          isClientAgreed: formData.isClientAgreed === "true",
          agentStatement: formData.agentStatement ?? null,
          statementDate: formData.statementDate ? formData.statementDate.toISOString() : null,
        },
      },
      {
        onSuccess: async () => {
          try {
            const growUpContractingLink = await fetchGrowUpContractingLink();
            if (growUpContractingLink?.url) {
              window.location.href = growUpContractingLink.url;
            } else {
              toast.error(t(Resources.AnalysisDetail.Toast.GrowUpRedirectError));
              onClose();
            }
          } catch {
            toast.error(t(Resources.AnalysisDetail.Toast.GrowUpRedirectError));
            onClose();
          }
        },
        onError,
      },
    );
  };

  const isBusy = isSaving || isRejecting;
  const saveButton = (
    <Button variant="contained" type="submit" loading={isBusy} disabled={isBusy}>
      {isClientDisagreed ? t(r.EndNegotiation) : t(r.ContinueToGrowUp)}
    </Button>
  );

  const content = isLoading ? (
    <Loader />
  ) : isError ? (
    <Empty
      title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
      text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
      iconSize={40}
      icon={<ErrorIcon sx={{ color: "grey.400" }} />}
    />
  ) : (
    <Stack spacing={Spacing.XL}>
      <Stack spacing={Spacing.XXL}>
        <FormTextField
          control={control}
          name="suitabilityInvestmentPlan"
          label={t(r.SuitabilityInvestmentPlan)}
          required={!isClientDisagreed}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityInvestmentPlanPlaceholder)}
          fullWidth
        />
        <FormTextField
          control={control}
          name="suitabilityKnowledgeAndExperience"
          label={t(r.SuitabilityKnowledgeAndExperience)}
          required={!isClientDisagreed}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityKnowledgeAndExperiencePlaceholder)}
          fullWidth
        />
        <FormTextField
          control={control}
          name="suitabilityInvestmentHorizon"
          label={t(r.SuitabilityInvestmentHorizon)}
          required={!isClientDisagreed}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityInvestmentHorizonPlaceholder)}
          fullWidth
        />
        <FormTextField
          control={control}
          name="suitabilityFinanceSituation"
          label={t(r.SuitabilityFinanceSituation)}
          required={!isClientDisagreed}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityFinanceSituationPlaceholder)}
          fullWidth
        />
        <FormTextField
          control={control}
          name="suitabilityRiskTolerance"
          label={t(r.SuitabilityRiskTolerance)}
          required={!isClientDisagreed}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityRiskTolerancePlaceholder)}
          fullWidth
        />
        <FormTextField
          control={control}
          name="suitabilityESG"
          label={t(r.SuitabilityESG)}
          required={!isClientDisagreed}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityESGPlaceholder)}
          fullWidth
        />
        <FormTextField
          control={control}
          name="suitabilityAdditionalStatement"
          label={t(r.SuitabilityAdditionalStatement)}
          maxLength={MAX_TEXT_LENGTH}
          minLength={MIN_TEXT_LENGTH}
          placeholder={t(r.SuitabilityAdditionalStatementPlaceholder)}
          fullWidth
        />
        <Divider />
        <Typography variant="h3">{"Vyjádření klienta"}</Typography>
        <FormRadioGroup
          control={control}
          name="isClientAgreed"
          label={t(r.IsClientAgreed)}
          options={toRadioOptions([
            { code: "true", name: t(r.IsClientAgreedOptionA) },
            { code: "false", name: t(r.IsClientAgreedOptionB) },
          ])}
          defaultValue="true"
          columns={1}
        />
        {isClientDisagreed && (
          <FormTextField
            control={control}
            name="agentStatement"
            label={t(r.AgentStatement)}
            required
            maxLength={MAX_TEXT_LENGTH}
            minLength={MIN_TEXT_LENGTH}
            placeholder={t(r.AgentStatementPlaceholder)}
            multiline
            rows={3}
            fullWidth
          />
        )}
        <FormDatePicker
          control={control}
          name="statementDate"
          label={t(r.StatementDate)}
          required
          disableFuture
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Stack>
    </Stack>
  );

  return (
    <FormProvider {...methods}>
      <DialogModal
        open={isOpen}
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        title={t(r.Title)}
        subtitle={<Alert severity="info">{t(r.TitleDescription)}</Alert>}
        content={content}
        onSubmit={handleSubmit(onSave)}
        primaryAction={saveButton}
        size="lg"
      />
    </FormProvider>
  );
};
